import "ress/ress.css";
import "react-modern-drawer/dist/index.css";

import { useEffect, useState } from "react";
import type { AppProps } from "next/app";
import { Provider } from "react-redux";
import { init } from "@amplitude/analytics-browser";

import Header from "../components/Header";
import Footer from "../components/Footer";

import { store } from "../utils/store";
import settings from "../constants/settings";
import Analytics from "../components/Analytics";
import EmailPopUp from "../components/EmailPopUp";
import GlobalStyle from "../components/GlobalStyle";
import { FlashPCBComponentType } from "../models/page";
import { Page } from "../constants/Styles";
import Authorization from "../components/Authorization";
import PartsSubscriptionProvider from "../utils/hooks/commonly-stored/PartsSubscriptionProvider";
import { StyleSheetManager } from "styled-components";
import isPropValid from "@emotion/is-prop-valid";
import { StyledTarget } from "styled-components/dist/types";
import { Amplify } from "aws-amplify";
import FlashPCBSWRConfig from "../components/FlashPCBSWRConfig";
import EnsureLatest from "../components/EnsureLatest";
import Banner from "../components/Banner";
import { Confirm, Alert, Sounds, Options } from "../components/alert";

function shouldForwardProp(
  prop: string,
  elementToBeCreated: StyledTarget<"web">,
) {
  if (typeof elementToBeCreated === "string") {
    // For HTML elements, forward the prop if it is a valid HTML attribute
    return isPropValid(prop);
  }
  // For other elements, forward all props
  return true;
}

Amplify.configure(
  {
    Auth: {
      Cognito: {
        userPoolId: settings.cognitoUserPoolId,
        userPoolClientId: settings.cognitoUserPoolWebClientId,
      },
    },
  },
  { ssr: true },
);

function MyApp({
  Component,
  pageProps,
}: AppProps & { Component: FlashPCBComponentType }) {
  const [isMenuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    init(settings.amplitudeApiKey, { autocapture: true });
  }, []);

  return (
    <StyleSheetManager shouldForwardProp={shouldForwardProp}>
      {Component.GlobalStyle ? <Component.GlobalStyle /> : <GlobalStyle />}
      <Provider store={store}>
        <PartsSubscriptionProvider>
          <FlashPCBSWRConfig>
            <Analytics />
            <EmailPopUp isMenuOpen={isMenuOpen} />
            <EnsureLatest />
            <Banner />
            {Component.Header ? (
              <Component.Header
                setMenuOpen={setMenuOpen}
                isMenuOpen={isMenuOpen}
              />
            ) : (
              <Header setMenuOpen={setMenuOpen} isMenuOpen={isMenuOpen} />
            )}
            <Page>
              <Authorization authOptions={Component.authOptions}>
                <Component {...pageProps} />
              </Authorization>
            </Page>
            <Footer />
            <Confirm />
            <Alert />
            <Sounds />
            <Options />
          </FlashPCBSWRConfig>
        </PartsSubscriptionProvider>
      </Provider>
    </StyleSheetManager>
  );
}
export default MyApp;
